<template lang="pug">
div.p-textfield(:class="classesDoContainer" :style="estiloDoContainer")
  PontotelInput(
    v-bind="atributosLocais"
    :modelValue="state"
    @update:modelValue="aoAtualizarValor"
    ref="input"
    :desabilitado="disabled || loading"
    :tipo="tipoCompativel"
    :nome="nome"
    :id="id"
    :label="hideLabel ? '' : label"
    :placeholder="placeholder"
    :erro="error"
    :infoInferior="mensagemDeInformacao || null"
    :obrigatorio="required"
    :somenteLeitura="somenteLeitura"
    :sankhya="possuiLogoSankhya"
    largura="100%"
    :autocomplete="autocomplete"
    spellcheck="false"
    v-on="escutadoresDeEventosLocais"
  )
    template(#default v-if="possuiSlotDeIcone || copyicon")
      span.p-textfield__acoes
        span.p-textfield__icon--span(v-if="possuiSlotDeIcone")
          slot(name="icon")
        span.p-textfield__icon--span-copy(v-if="copyicon" @click.stop="copyToClipBoard")
          tooltip(
            :text="$t('compartilhados.inputs.PTextField.copiado')"
            trigger="manual"
            v-model="showTooltip"
          )
            img.p-textfield__icon(src="../../../static/images/copy_content.svg")

</template>

<script>
import Tooltip from 'src/common/uiv/PttTooltip'
import { PontotelInput } from '@Lar21/imaginii-web-components/lib/vue'
import { handlerUseStateVmodel } from '@/common/mixins/handlerUseStateVmodel'
import { elementFocusable } from '@/common/mixins/elementFocusable'

const TIPOS_SUPORTADOS = ['number', 'text', 'email', 'password', 'tel', 'date']

export default {
  name: 'PTextField',
  inheritAttrs: false,
  emits: ['update:modelValue', 'change'],
  mixins: [handlerUseStateVmodel(), elementFocusable('input')],
  components: {
    PontotelInput,
    Tooltip
  },
  data () {
    return {
      showTooltip: false
    }
  },
  props: {
    label: { type: String, required: true },
    hideLabel: Boolean,
    disabled: Boolean,
    loading: Boolean,
    error: String,
    sinalizarErroSemMensagem: { type: Boolean, required: false, default: false },
    copyicon: Boolean,
    required: { type: Boolean, 'default': false },
    type: { default: 'text' },
    autocomplete: { default: 'off' },
    parser: { type: String, default: 'number' },
    estilo: { type: String },
    mensagemDeInformacao: { type: String, default: '' }
  },
  computed: {
    possuiSlotDeIcone () {
      const slotDeIcone = this.$slots.icon

      if (!slotDeIcone) return false

      return typeof slotDeIcone === 'function' || Boolean(slotDeIcone.length)
    },
    possuiLogoSankhya () {
      return Boolean(this.$slots.logoIcon && this.mensagemDeInformacao)
    },
    state: {
      set (val) {
        if (this.type === 'number') {
          if (this.parser === 'number') {
            val = parseInt(val)
          } else if (this.parser === 'float') {
            val = parseFloat(val)
          }
        }
        this.__state = val
      },
      get () {
        return this.__state
      }
    },
    tipoCompativel () {
      return TIPOS_SUPORTADOS.includes(this.type) ? this.type : 'text'
    },
    nome () {
      return this.$attrs.name || ''
    },
    id () {
      return this.$attrs.id
    },
    placeholder () {
      return this.$attrs.placeholder ?? ' '
    },
    somenteLeitura () {
      return Object.prototype.hasOwnProperty.call(this.$attrs, 'readonly') && this.$attrs.readonly !== false
    },
    escutadoresDeEventosLocais () {
      return Object.keys(this.$attrs)
        .filter(chave => chave.startsWith('on'))
        .reduce((resultado, chave) => {
          const nomeDoEvento = chave
            .replace(/^on/, '')
            .replace(/^[A-Z]/, letra => letra.toLowerCase())

          if (!['input', 'change', 'update:modelValue'].includes(nomeDoEvento)) {
            resultado[nomeDoEvento] = this.$attrs[chave]
          }

          return resultado
        }, {})
    },
    atributosLocais () {
      const listaDeChavesASeremFiltradas = [
        'class',
        'disabled',
        'id',
        'modelValue',
        'name',
        'placeholder',
        'readonly',
        'style',
        'type'
      ]

      return Object.keys(this.$attrs)
        .filter(chave => !listaDeChavesASeremFiltradas.includes(chave))
        .filter(chave => !chave.startsWith('on'))
        .reduce((resultado, atributo) => {
          resultado[atributo] = this.$attrs[atributo]
          return resultado
        }, {})
    },
    classesDoContainer () {
      return [
        this.$attrs.class,
        {
          'icon-slot': this.possuiSlotDeIcone,
          'p-textfield--erro-sem-mensagem': this.sinalizarErroSemMensagem,
          [this.estilo]: this.estilo
        }
      ]
    },
    estiloDoContainer () {
      return this.$attrs.style
    }
  },
  methods: {
    aoAtualizarValor (valor) {
      this.state = valor
    },
    obterInputNativo () {
      return this.$refs.input?.inputRef
    },
    copyToClipBoard () {
      const input = this.obterInputNativo()
      if (!input) return

      input.select()
      document.execCommand('copy')

      if (window.getSelection) {
        window.getSelection().removeAllRanges()
      } else if (document.selection) {
        document.selection.empty()
      }

      this.showTooltip = true
      setTimeout(() => {
        this.showTooltip = false
      }, 1000)
    },
    select () {
      // to outside use, dont remove, we want this component to act like a real input
      this.obterInputNativo()?.select()
    }
  }
}
</script>

<style scoped lang="scss">
.p-textfield {
  --p-textfield-altura-controle: 44px;
  --p-textfield-fonte-controle: 16px;
  --p-textfield-fonte-label: 12px;
  --p-textfield-linha-controle: 20px;

  display: grid;
  box-sizing: border-box;
  position: relative;
  text-align: start;
  line-height: 1.3;
  width: 100%;

  &.icon-slot {
    grid-template-columns: minmax(0, 1fr) min-content;
    column-gap: 8px;
  }

  input.p-textfield__input {
    display: block;
    width: 100%;
    min-height: var(--p-textfield-altura-controle);
    box-sizing: border-box;
    padding-top: 18px;
    padding-bottom: 5px;
    font-size: var(--p-textfield-fonte-controle);
    line-height: var(--p-textfield-linha-controle);
    font-family: var(--texto-tipografia, var(--front-font));
    border: none;
    border-radius: var(--input-arredondamento-m-box, 0);
    outline: none;
    color: var(--texto-forte);
    border-bottom-width: 1px;
    border-bottom-style: solid;
    border-bottom-color: var(--input-cor-habilitado-linha-default, var(--color-primary));
    background-color: transparent;
    transition: border-color .2s ease, color .2s ease;

    &::-moz-selection {
      /* Code for Firefox */
      color: white;
      background: var(--color-primary);
    }

    &::selection {
      color: white;
      background: var(--color-primary);
    }

    &::placeholder {
      color: var(--input-cor-habilitado-conteudo-vazio, var(--texto-suave));
      font-size: var(--p-textfield-fonte-controle);
    }

    &:hover:not(:disabled) {
      border-bottom-color: var(--input-cor-habilitado-linha-hover, var(--color-primary-hover));
    }

    &:focus {
      /* linha material no foco = plum (acao/primary), consistente com o label focado (l. abaixo); nao teal/info */
      border-bottom-color: var(--input-cor-habilitado-linha-foco, var(--color-primary));
    }

    /* Efeito de subir o texto*/
    &:not(:placeholder-shown)+.p-textfield__title .p-textfield__title-text {
      transform: translateY(0);
      font-size: var(--p-textfield-fonte-label);
      line-height: 16px;
      color: var(--input-cor-habilitado-label-externo-default, var(--texto-suave));
    }

    &:focus+.p-textfield__title .p-textfield__title-text {
      transform: translateY(0);
      font-size: var(--p-textfield-fonte-label);
      line-height: 16px;
      color: var(--input-cor-habilitado-linha-clicado, var(--color-primary));
      white-space: normal;
      overflow: unset;
      text-overflow: unset;
    }

    /* Disabled style */
    &:disabled {
      border-bottom-color: var(--color-default-disabled);
      border-bottom-style: solid;
      color: var(--texto-suave);
      background-color: transparent;
    }

    &:disabled+.p-textfield__title {
      color: var(--texto-suave);
    }

    &.error {
      border-bottom-color: var(--input-cor-habilitado-linha-erro, var(--color-danger));

      :focus+.p-textfield__title .p-textfield__title-text {
        color: var(--input-cor-habilitado-linha-erro, var(--color-danger));
      }

      &:focus+.p-textfield--required .p-textfield__title-text::after {
        color: var(--input-cor-habilitado-linha-erro, var(--color-danger));
      }
    }

    &.sinalizarErroSemMensagem {
      border-bottom-color: var(--color-danger);
      color: var(--color-danger);
    }

    &.novoinfo {
      font-family: 'Hanken Grotesk';

      &:focus,
      &:active {
        border-bottom-color: var(--input-cor-habilitado-linha-clicado, var(--color-primary));
      }
    }

    /* this disables the default browser arrows in input number */
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      /* display: none; <- Crashes Chrome on hover */
      -webkit-appearance: none;
      margin: 0;
      /* <-- Apparently some margin are still there even though it's hidden */
    }

    &[type=number] {
      -moz-appearance: textfield;
      /* Firefox */
    }

  }

  label.p-textfield__title {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: var(--p-textfield-altura-controle);
    font-size: var(--p-textfield-fonte-controle);
    font-family: var(--texto-tipografia, var(--front-font));
    pointer-events: none;

    span.p-textfield__title-text {
      display: flex;
      align-items: baseline;
      gap: 3px;
      position: relative;
      transform: translateY(20px);
      line-height: var(--p-textfield-linha-controle);
      transition: transform .2s ease-out, color .2s ease-out, font-size .2s ease-out;
      width: 100%;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      .p-textfield__title-label {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .p-textfield__title-required {
        color: var(--color-danger);
        flex: none;
        font-size: .8em;
      }

      &.novoinfo {
        font-family: "Hanken Grotesk";
      }
    }

    /* Error style */
    &.error {
      color: var(--color-danger);

      &:focus {
        border-bottom-color: var(--color-danger);
      }
    }
  }
}

/* Sankhya style */

// Se a div pai tiver a classe sankhya e o input estiver desabilitado, usar o token de conteudo desabilitado.
.campoBloqueadoSankhya input:disabled + .p-textfield__title .p-textfield__title-text {
  color: var(--input-cor-desabilitado-conteudo, var(--color-default-disabled));
}
</style>
<style scoped>
/* Cria efeito de linha mudar de cor quando selecionado */

.p-textfield__error--message {
  display: block;
  font-size: .75rem;
  padding-top: 2px;
  padding-bottom: 2px;
  color: var(--color-danger);
}

.p-textfield__error--message {
  display: block;
  font-size: .75rem;
  padding-top: 2px;
  padding-bottom: 2px;
  color: var(--color-danger);
}

.p-textfield__error--placeholder {
  color: transparent;
  font-size: .75rem;
  padding-top: 2px;
  padding-bottom: 2px;
  grid-row: 2;
  display: block;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

/* Copy Icon style */
.p-textfield__icon {
  margin-right: 6px;
  position: relative;
  cursor: pointer;
  width: 15px;
}

.p-textfield__icon--span {
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: start;
  min-height: var(--p-textfield-altura-controle);
}

.p-textfield__icon--span :deep(.botao-icone) {
  min-height: 32px;
  padding: 0 10px;
}

.p-textfield__icon--span-copy {
  position: absolute;
  display: grid;
  right: 0;
  height: 100%;
  align-items: center;
  visibility: hidden;
}

.p-textfield:hover .p-textfield__icon--span-copy {
  visibility: visible !important;
}

.error {
  grid-row: 2;
}

/* Logo label style */
.p-textfield__logoicon {
  margin: 0px 3px 0px 5px;
}

.EspacoEmbaixo {
  margin-bottom: 12px;
}
</style>

<style scoped lang="scss">
.p-textfield {
  &.icon-slot {
    grid-template-columns: minmax(0, 1fr);
    column-gap: 0;
  }

  :deep(.pontotel-input) {
    width: 100%;
  }

  &--erro-sem-mensagem :deep(.pontotel-input__campo) {
    border-color: var(--input-cor-habilitado-linha-erro, var(--color-danger));
  }
}

.p-textfield__acoes {
  display: flex;
  align-items: center;
}

.p-textfield__icon--span-copy {
  position: static;
  height: auto;
}
</style>
